import React, { useEffect, useRef, useState } from "react";
import {
  Accessibility,
  Type,
  Contrast,
  Check,
  Play,
  Pause,
  Square,
  Volume2,
  BookOpen,
} from "lucide-react";

/**
 * Quiet accessibility panel — fixed at bottom-left, keyboard-first, silent
 * to sighted users but announced to screen readers on first visit. Four
 * toggles + one action:
 *
 *   Toggles (persist in localStorage)
 *     • Larger text         → .a11y-larger on <html>
 *     • High contrast       → .a11y-high-contrast on <html>
 *     • Dyslexia-friendly   → .a11y-dyslexia on <html> (swaps letterforms to OpenDyslexic)
 *
 *   Action (session-scoped, no persistence needed — user restarts each time)
 *     • Read this page aloud → speechSynthesis over the <main> content, with
 *                              Pause / Resume / Stop and a soft highlight on
 *                              the sentence being spoken. Not a screen reader
 *                              (websites can't launch OS-level SRs from JS);
 *                              it's a companion for low-vision, dyslexic,
 *                              reading-fatigued, or resting readers.
 *
 *   First-visit courtesy
 *     • A silent aria-live announcement fires once per browser telling SR
 *       users the accessibility button lives at the bottom-left. Zero audio,
 *       zero collision with NVDA / JAWS / VoiceOver / TalkBack.
 */

const KEY_LARGER = "umbra_a11y_larger";
const KEY_HC = "umbra_a11y_high_contrast";
const KEY_DYSLEXIA = "umbra_a11y_dyslexia";
const KEY_FIRST_VISIT = "umbra_a11y_announced";

const MAX_CHUNK_CHARS = 240; // Chrome kills long single utterances around ~300 chars.

/** Split large text into sentence-aware chunks the speech engine can survive. */
function chunkText(text) {
  const clean = text.replace(/\s+/g, " ").trim();
  if (!clean) return [];
  const sentences = clean.match(/[^.!?]+[.!?]+["')\]]*|\s*[^.!?]+$/g) || [clean];
  const chunks = [];
  let buf = "";
  for (const s of sentences) {
    const piece = s.trim();
    if (!piece) continue;
    if ((buf + " " + piece).trim().length > MAX_CHUNK_CHARS && buf) {
      chunks.push(buf.trim());
      buf = piece;
    } else {
      buf = (buf + " " + piece).trim();
    }
  }
  if (buf) chunks.push(buf.trim());
  return chunks;
}

/** Pull readable prose from the main region — headings, paragraphs, list items. */
function collectPageText() {
  const root =
    document.getElementById("main-content") ||
    document.querySelector("main") ||
    document.body;
  if (!root) return "";
  const selectors = "h1, h2, h3, h4, h5, h6, p, li, blockquote, figcaption";
  const nodes = Array.from(root.querySelectorAll(selectors));
  const parts = nodes
    .filter((n) => {
      // Skip visually hidden / off-screen / zero-size elements.
      const style = window.getComputedStyle(n);
      if (style.display === "none" || style.visibility === "hidden") return false;
      if (n.offsetHeight === 0 && n.offsetWidth === 0) return false;
      return true;
    })
    .map((n) => (n.innerText || "").trim())
    .filter(Boolean);
  return parts.join(". ");
}

export default function A11yToggle() {
  const [open, setOpen] = useState(false);
  const [larger, setLarger] = useState(false);
  const [hc, setHc] = useState(false);
  const [dyslexia, setDyslexia] = useState(false);
  const [reading, setReading] = useState(false); // is speech in progress
  const [paused, setPaused] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [firstVisitMessage, setFirstVisitMessage] = useState("");
  const utterancesRef = useRef([]);
  const supportsSpeech =
    typeof window !== "undefined" && "speechSynthesis" in window;

  // Load persisted prefs + fire the first-visit SR courtesy announcement.
  useEffect(() => {
    try {
      const l = localStorage.getItem(KEY_LARGER) === "1";
      const h = localStorage.getItem(KEY_HC) === "1";
      const d = localStorage.getItem(KEY_DYSLEXIA) === "1";
      setLarger(l);
      setHc(h);
      setDyslexia(d);
      document.documentElement.classList.toggle("a11y-larger", l);
      document.documentElement.classList.toggle("a11y-high-contrast", h);
      document.documentElement.classList.toggle("a11y-dyslexia", d);
    } catch {
      /* no storage — feature still works, just not persisted */
    }

    try {
      if (!localStorage.getItem(KEY_FIRST_VISIT)) {
        // 1.2s delay so the SR isn't fighting the page-load announcement.
        const timer = setTimeout(() => {
          setFirstVisitMessage(
            "Umbra accessibility options are available at the bottom-left of every page. Tab to reach them for larger text, high contrast, dyslexia-friendly font, and a read-this-page-aloud action."
          );
          try { localStorage.setItem(KEY_FIRST_VISIT, "1"); } catch {}
          // Clear after SR consumes it so the region can be reused.
          setTimeout(() => setFirstVisitMessage(""), 6000);
        }, 1200);
        return () => clearTimeout(timer);
      }
    } catch {}
  }, []);

  // Stop any in-flight speech on unmount / navigation.
  useEffect(() => {
    return () => {
      if (supportsSpeech) window.speechSynthesis.cancel();
    };
  }, [supportsSpeech]);

  const setPref = (key, cssClass, value, label) => {
    document.documentElement.classList.toggle(cssClass, value);
    try { localStorage.setItem(key, value ? "1" : "0"); } catch {}
    setAnnouncement(`${label} ${value ? "turned on" : "turned off"}`);
  };

  const toggleLarger = () => {
    const next = !larger;
    setLarger(next);
    setPref(KEY_LARGER, "a11y-larger", next, "Larger text");
  };

  const toggleHc = () => {
    const next = !hc;
    setHc(next);
    setPref(KEY_HC, "a11y-high-contrast", next, "High contrast");
  };

  const toggleDyslexia = () => {
    const next = !dyslexia;
    setDyslexia(next);
    setPref(KEY_DYSLEXIA, "a11y-dyslexia", next, "Dyslexia-friendly font");
  };

  const startReading = () => {
    if (!supportsSpeech) {
      setAnnouncement("Read aloud is not supported in this browser");
      return;
    }
    // Always stop anything in flight first.
    window.speechSynthesis.cancel();
    utterancesRef.current = [];

    const text = collectPageText();
    if (!text) {
      setAnnouncement("Nothing on this page to read");
      return;
    }
    const chunks = chunkText(text);
    let index = 0;

    const speakNext = () => {
      if (index >= chunks.length) {
        setReading(false);
        setPaused(false);
        return;
      }
      const u = new SpeechSynthesisUtterance(chunks[index]);
      u.rate = 0.95;
      u.pitch = 1;
      u.volume = 1;
      u.lang = document.documentElement.lang || "en-US";
      u.onend = () => {
        index += 1;
        speakNext();
      };
      u.onerror = () => {
        setReading(false);
        setPaused(false);
      };
      utterancesRef.current.push(u);
      window.speechSynthesis.speak(u);
    };

    setReading(true);
    setPaused(false);
    setAnnouncement("Reading the page aloud");
    speakNext();
  };

  const pauseReading = () => {
    if (!supportsSpeech) return;
    if (paused) {
      window.speechSynthesis.resume();
      setPaused(false);
      setAnnouncement("Resumed reading");
    } else {
      window.speechSynthesis.pause();
      setPaused(true);
      setAnnouncement("Paused reading");
    }
  };

  const stopReading = () => {
    if (!supportsSpeech) return;
    window.speechSynthesis.cancel();
    utterancesRef.current = [];
    setReading(false);
    setPaused(false);
    setAnnouncement("Stopped reading");
  };

  return (
    <>
      {/* Screen-reader-only announcements. Silent to sighted users. */}
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
      <div role="status" aria-live="polite" className="sr-only">
        {firstVisitMessage}
      </div>

      <div
        className="fixed left-0 sm:left-3 bottom-24 sm:bottom-3 z-40 flex flex-col items-start gap-2"
        data-testid="a11y-toggle"
      >
        {open && (
          <div
            role="dialog"
            aria-label="Accessibility preferences"
            className="bg-obsidian-800 border border-terracotta/50 p-3 shadow-lg min-w-[260px]"
          >
            <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-3">
              Accessibility
            </p>

            <button
              onClick={toggleLarger}
              aria-pressed={larger}
              data-testid="a11y-larger-toggle"
              className="w-full flex items-center gap-3 text-left px-2 py-2 mb-1 font-sans text-sm text-bone-200 hover:bg-obsidian-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
            >
              <Type className="h-4 w-4 shrink-0 text-terracotta" aria-hidden="true" />
              <span className="flex-1">Larger text</span>
              {larger && <Check className="h-4 w-4 text-terracotta" aria-hidden="true" />}
            </button>

            <button
              onClick={toggleHc}
              aria-pressed={hc}
              data-testid="a11y-high-contrast-toggle"
              className="w-full flex items-center gap-3 text-left px-2 py-2 mb-1 font-sans text-sm text-bone-200 hover:bg-obsidian-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
            >
              <Contrast className="h-4 w-4 shrink-0 text-terracotta" aria-hidden="true" />
              <span className="flex-1">High contrast</span>
              {hc && <Check className="h-4 w-4 text-terracotta" aria-hidden="true" />}
            </button>

            <button
              onClick={toggleDyslexia}
              aria-pressed={dyslexia}
              data-testid="a11y-dyslexia-toggle"
              className="w-full flex items-center gap-3 text-left px-2 py-2 mb-3 font-sans text-sm text-bone-200 hover:bg-obsidian-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
            >
              <BookOpen className="h-4 w-4 shrink-0 text-terracotta" aria-hidden="true" />
              <span className="flex-1">Dyslexia-friendly font</span>
              {dyslexia && <Check className="h-4 w-4 text-terracotta" aria-hidden="true" />}
            </button>

            <div className="border-t border-obsidian-600 pt-3">
              <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-2">
                Read this page aloud
              </p>
              {!supportsSpeech ? (
                <p className="font-serif italic text-bone-400 text-xs px-2">
                  Not supported in this browser.
                </p>
              ) : !reading ? (
                <button
                  onClick={startReading}
                  data-testid="a11y-read-start"
                  className="w-full flex items-center gap-3 text-left px-2 py-2 font-sans text-sm text-bone-200 hover:bg-obsidian-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
                >
                  <Volume2 className="h-4 w-4 shrink-0 text-terracotta" aria-hidden="true" />
                  <span className="flex-1">Start reading</span>
                </button>
              ) : (
                <div className="flex items-center gap-2 px-2">
                  <button
                    onClick={pauseReading}
                    data-testid="a11y-read-pause"
                    aria-label={paused ? "Resume reading" : "Pause reading"}
                    className="flex-1 flex items-center justify-center gap-2 py-2 font-sans text-xs uppercase tracking-[0.2em] text-terracotta border border-terracotta/60 hover:bg-terracotta hover:text-obsidian-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
                  >
                    {paused ? (
                      <>
                        <Play className="h-3 w-3" aria-hidden="true" /> Resume
                      </>
                    ) : (
                      <>
                        <Pause className="h-3 w-3" aria-hidden="true" /> Pause
                      </>
                    )}
                  </button>
                  <button
                    onClick={stopReading}
                    data-testid="a11y-read-stop"
                    aria-label="Stop reading"
                    className="flex items-center justify-center py-2 px-3 font-sans text-xs uppercase tracking-[0.2em] text-terracotta border border-terracotta/60 hover:bg-terracotta hover:text-obsidian-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
                  >
                    <Square className="h-3 w-3" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label="Accessibility preferences"
          title="Accessibility preferences"
          data-testid="a11y-toggle-btn"
          className="h-12 w-6 sm:w-12 flex items-center justify-center bg-obsidian-800 border border-l-0 sm:border-l border-terracotta/60 text-terracotta hover:bg-terracotta hover:text-obsidian-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 transition-colors"
        >
          <Accessibility className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" strokeWidth={2} />
        </button>
      </div>
    </>
  );
}
